import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { User, SiteUser } from "./types";

export interface UserManagementState {
    users: User[];
    siteUsers: SiteUser[];
    selectedUser: User | null;
    selectedSiteUser: SiteUser | null;
    errorMessage?: string;
    isModalOpen: boolean;
    roles: string[];
    siteRoles: string[];
    sites: { name: string; uuid: string }[];
    trials: { name: string; uuid: string }[];
}

export const initialState: UserManagementState = {
    users: [],
    siteUsers: [],
    selectedUser: null,
    selectedSiteUser: null,
    isModalOpen: false,
    roles: [],
    siteRoles: ["Site User", "CRC"],
    sites: [],
    trials: [],
};

export const userManagementSlice = createSlice({
    name: "userManagement",
    initialState,
    reducers: {
        setUsers: (state, action: PayloadAction<{ usersFromSites: SiteUser[]; usersFromTrials: User[] }>) => {
            state.siteUsers = action.payload.usersFromSites;
            state.users = action.payload.usersFromTrials;
        },
        setRoles: (state, action: PayloadAction<string[]>) => {
            state.roles = action.payload.filter((role) => !state.siteRoles.includes(role));
        },
        setSites: (state, action: PayloadAction<{ name: string; uuid: string }[]>) => {
            state.sites = action.payload;
        },
        setTrialsUserManagement: (state, action: PayloadAction<{ name: string; uuid: string }[]>) => {
            state.trials = action.payload;
        },
        addUser: (state, action: PayloadAction<User>) => {
            state.users.push(action.payload);
        },
        addSiteUser: (state, action: PayloadAction<SiteUser>) => {
            state.siteUsers.push(action.payload);
        },
        updateUser: (state, action: PayloadAction<User>) => {
            const index = state.users.findIndex((user) => user.email === action.payload.email);
            if (index !== -1) {
                state.users[index] = action.payload;
            }
        },
        updateSiteUser: (state, action: PayloadAction<SiteUser>) => {
            const index = state.siteUsers.findIndex((user) => user.email === action.payload.email);
            if (index !== -1) {
                state.siteUsers[index] = action.payload;
            }
        },
        addSIte: (state, action: PayloadAction<{ name: string; uuid: string }>) => {
            state.sites.push(action.payload);
        },
    },
});

export const {
    setUsers,
    setRoles,
    setSites,
    setTrialsUserManagement,
    addUser,
    addSiteUser,
    updateUser,
    updateSiteUser,
} = userManagementSlice.actions;

// Selectors
export const selectUserManagement = (state: RootState) => {
    return state.userManagement;
};

export const selectUsers = (state: RootState) => {
    return state.userManagement.users;
};

export const selectSiteUsers = (state: RootState) => {
    return state.userManagement.siteUsers;
};

export const selectRoles = (state: RootState) => {
    return state.userManagement.roles;
};

export const selectSiteRoles = (state: RootState) => {
    return state.userManagement.siteRoles;
};

export const selectTrialsUserManagement = (state: RootState) => {
    return state.userManagement.trials;
};
export const selectSites = (state: RootState) => {
    return state.userManagement.sites;
};

export const selectSelectedUser = (state: RootState) => {
    return state.userManagement.selectedUser;
};

export const selectSelectedSiteUser = (state: RootState) => {
    return state.userManagement.selectedSiteUser;
};

export const selectIsModalOpen = (state: RootState) => {
    return state.userManagement.isModalOpen;
};

export default userManagementSlice.reducer;
